// src/sectors/pilates/reports/staff/components/StaffSessionsAccordion.jsx
import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

/* ---------------- helpers ---------------- */
// ✅ kolon şeması (header + satır %100 aynı hizaya gelsin)
const SUMMARY_COLS = [
  { w: 86 }, // Tarih
  { w: 58 }, // Saat
  { w: 160 }, // Aktivite
  { w: 110 }, // Tip
  { w: 92 }, // Grup
  { w: 360 }, // Katılımcılar
  { w: 150 }, // Yoklama
  { w: 110 }, // Ödenen
  { w: 110 }, // Borç
  { w: 46 }, // expand icon boşluğu
];

function ColGroup({ cols }) {
  return (
    <colgroup>
      {cols.map((c, i) => (
        <col key={i} style={{ width: c.w }} />
      ))}
    </colgroup>
  );
}
function fmtDateTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}
function fmtTimeTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function safeText(v, fallback = '—') {
  const s = (v ?? '').toString().trim();
  return s ? s : fallback;
}
function short(v, n = 18) {
  const s = safeText(v, '');
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
function fmtTry(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('tr-TR')} ₺`;
}
function attendanceChip(status) {
  const v = String(status ?? '').toUpperCase();
  if (v === 'ATTENDED') return { label: 'Katıldı', color: 'success' };
  if (v === 'NO_SHOW') return { label: 'Gelmedi', color: 'error' };
  if (v === 'CANCELLED') return { label: 'İptal', color: 'warning' };
  if (v === 'PENDING') return { label: 'Bekliyor', color: 'default' };
  if (v) return { label: v, color: 'default' };
  return { label: '—', color: 'default' };
}
function paymentLabel(v) {
  const s = String(v ?? '').toUpperCase();
  if (s === 'CASH') return 'Nakit';
  if (s === 'CARD') return 'Kart';
  if (s === 'TRANSFER') return 'Havale';
  if (s === 'PACKAGE') return 'Paket';
  return s || '—';
}
function usageLabel(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (Number.isFinite(used) && Number.isFinite(total) && total > 0) return `${used} / ${total}`;
  if (Number.isFinite(used)) return String(used);
  return null;
}
function usageColor(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 'default';
  if (used >= total) return 'error';
  if (used >= total - 1) return 'warning';
  return 'info';
}

/* ✅ 2 kişiye kadar isim + ... + sayı */
function participantsPreview(participants = [], maxNames = 2) {
  const arr = (participants || []).map((p) => safeText(p?.memberName, '')).filter(Boolean);

  const total = arr.length;
  if (total === 0) return { text: '—', total: 0 };

  const shown = arr.slice(0, maxNames);
  const more = total - shown.length;

  const text = more > 0 ? `${shown.join(', ')}…` : shown.join(', ');
  return { text, total, full: arr.join(', ') };
}

/* ✅ seans yoklama özeti: kaç kişi pending? kaç kişi alınmış? */
function attendanceSummary(participants = []) {
  const list = participants || [];
  if (list.length === 0) return { pending: 0, taken: 0, total: 0 };

  let pending = 0;
  let taken = 0;

  for (const p of list) {
    const st = String(p?.attendanceStatus ?? '').toUpperCase();
    if (st === 'PENDING') pending += 1;
    if (p?.attendanceTakenAt) taken += 1;
  }

  return { pending, taken, total: list.length };
}
function rsvpChip(status, reportedAt) {
  const s = String(status ?? '').toUpperCase();
  const hasTime = Boolean(reportedAt);

  // null / UNKNOWN / boş => işaretlemedi
  if (!s || s === 'UNKNOWN' || s === 'NULL') {
    return { label: 'İşaretlemedi', color: 'default', tip: hasTime ? reportedAt : null };
  }
  if (s === 'YES')
    return { label: 'Geleceğim', color: 'success', tip: hasTime ? reportedAt : null };
  if (s === 'NO')
    return { label: 'Gelmeyeceğim', color: 'error', tip: hasTime ? reportedAt : null };

  return { label: s, color: 'default', tip: hasTime ? reportedAt : null };
}

/* ---------------- group by session ---------------- */
function groupBySession(rows = []) {
  const map = new Map();

  for (const r of rows || []) {
    const key = r.sessionId;
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, {
        sessionId: r.sessionId,
        startAt: r.startAt,
        dayISO: r.dayISO,
        startMin: r.startMin,
        durationMin: r.durationMin,
        trainerName: r.trainerName,
        activityName: r.activityName,
        sessionType: r.sessionType,
        billingMode: r.billingMode,
        participants: [],
      });
    }
    map.get(key).participants.push(r);
  }

  const sessions = Array.from(map.values()).map((s) => {
    const anyDebt = (s.participants || []).some((p) => (Number(p?.planDebtTry) || 0) > 0);
    const totalPaid = (s.participants || []).reduce(
      (acc, p) => acc + (Number(p?.planPaidTry) || 0),
      0
    );
    const totalDebt = (s.participants || []).reduce(
      (acc, p) => acc + (Number(p?.planDebtTry) || 0),
      0
    );

    return {
      ...s,
      isGroup: (s.participants?.length || 0) > 1,
      anyDebt,
      totalPaid,
      totalDebt,
    };
  });

  sessions.sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
  return sessions;
}

/* ---------------- detail table (katılımcılar) ---------------- */
function DetailsTableHeader({ thSx }) {
  return (
    <MDBox component="thead">
      <MDBox component="tr" sx={{ bgcolor: 'grey.50' }}>
        <MDBox component="th" sx={{ ...thSx, width: 86 }}>
          Tarih
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 58 }}>
          Saat
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 180 }}>
          Üye
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 130 }}>
          Seans Türü
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Katılım
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 120 }}>
          Üye Beyanı
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 190 }}>
          Paket (Dönem)
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Kullanım
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 104 }}>
          Paket Fiyat
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 104 }}>
          Ödenen
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 96 }}>
          Ödeme
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 78 }}>
          Grup?
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 220 }}>
          Not
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 140 }}>
          Yoklama Zamanı
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 140 }}>
          Alan Kişi
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function DetailsTableRows({ rows, tdSx }) {
  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <MDBox component="tbody">
      {safeRows.map((r, i) => {
        const debt = Number(r?.planDebtTry) || 0;
        const isDebt = debt > 0;

        const att = attendanceChip(r.attendanceStatus);
        const rsvp = rsvpChip(r.rsvpStatus, r.rsvpReportedAt);
        const pkg = [safeText(r.packageName, ''), safeText(r.packagePeriod, '')]
          .filter(Boolean)
          .join(' • ');
        const usage = usageLabel(r);

        return (
          <MDBox
            key={r.participantId ?? `${r.sessionId}-${i}`}
            component="tr"
            sx={{
              ...(isDebt && {
                bgcolor: 'error.50',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
              }),
              '&:hover': { bgcolor: isDebt ? 'error.100' : 'grey.50' },
            }}
          >
            <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
              {fmtDateTR(r.startAt)}
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              {fmtTimeTR(r.startAt)}
            </MDBox>

            <MDBox component="td" sx={{ ...tdSx, fontWeight: 700 }}>
              <span title={safeText(r.memberName)}>{short(r.memberName, 24)}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              <span title={safeText(r.sessionType)}>{short(r.sessionType, 18)}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              <Chip size="small" variant="outlined" label={att.label} color={att.color} />
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              <span
                title={
                  r.rsvpReportedAt
                    ? `${fmtDateTR(r.rsvpReportedAt)} ${fmtTimeTR(r.rsvpReportedAt)}`
                    : rsvp.label
                }
              >
                <Chip size="small" variant="outlined" label={rsvp.label} color={rsvp.color} />
              </span>
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              <span title={pkg || '—'}>{pkg ? short(pkg, 34) : '—'}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {usage ? (
                <Chip size="small" variant="outlined" label={usage} color={usageColor(r)} />
              ) : (
                '—'
              )}
            </MDBox>

            <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
              {fmtTry(r.planPriceTry)}
            </MDBox>

            <MDBox
              component="td"
              sx={{
                ...tdSx,
                fontWeight: 800,
                color: (Number(r.planPaidTry) || 0) > 0 ? 'success.main' : 'text.primary',
              }}
            >
              {fmtTry(r.planPaidTry)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {paymentLabel(r.paymentMethod)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              <Chip
                size="small"
                variant="outlined"
                label={r.isGroup ? 'Grup' : 'Bireysel'}
                color={r.isGroup ? 'info' : 'default'}
              />
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {safeText(r.note)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {r.attendanceTakenAt ? (
                <>
                  {fmtDateTR(r.attendanceTakenAt)}
                  <br />
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    {fmtTimeTR(r.attendanceTakenAt)}
                  </MDTypography>
                </>
              ) : (
                '—'
              )}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {safeText(r.attendanceTakenByName)}
            </MDBox>
          </MDBox>
        );
      })}

      {safeRows.length === 0 && (
        <MDBox component="tr">
          <MDBox component="td" colSpan={15} sx={{ py: 1.5 }}>
            {' '}
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Katılımcı bulunamadı.
            </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}

/* ---------------- summary table (seanslar) ---------------- */
function SummaryHeader({ thSx }) {
  return (
    <MDBox component="thead">
      <MDBox component="tr" sx={{ bgcolor: 'grey.50' }}>
        <MDBox
          component="th"
          sx={{
            ...thSx,
            position: 'sticky',
            left: 0,
            zIndex: 6,
            backgroundColor: 'grey.50',
            boxShadow: '6px 0 8px -8px rgba(0,0,0,0.35)',
          }}
        >
          Tarih
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 58 }}>
          Saat
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 160 }}>
          Aktivite
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Tip
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Grup
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 360 }}>
          Katılımcılar
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 150 }}>
          Yoklama
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Ödenen
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Borç
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 46, textAlign: 'center' }}>
          {/* expand */}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function SummaryRow({ s, tdSx, expanded }) {
  const prev = participantsPreview(s.participants, 2);
  const attSum = attendanceSummary(s.participants);

  const yoklamaLabel =
    attSum.total === 0 ? '—' : attSum.pending > 0 ? `${attSum.pending} bekliyor` : 'Tamam';

  const yoklamaColor = attSum.total === 0 ? 'default' : attSum.pending > 0 ? 'warning' : 'success';

  return (
    <MDBox
      component="tr"
      sx={{
        bgcolor: expanded ? 'grey.100' : 'common.white',
        transition: 'background-color 140ms ease, transform 140ms ease',

        ...(s.anyDebt && {
          bgcolor: expanded ? 'error.100' : 'error.50',
        }),

        '&:hover': {
          bgcolor: s.anyDebt ? 'error.100' : 'grey.50',
        },
      }}
    >
      <MDBox
        component="td"
        sx={{
          ...tdSx,
          fontWeight: 900,

          // ✅ küçük ekranda da görünmesi için sabitle
          position: 'sticky',
          left: 0,
          zIndex: 5,
          position: 'sticky', // bunu tekrar etme

          // ✅ sabit kolon arkası dolu olsun (yoksa alttan kolonlar görünür)
          backgroundColor: s.anyDebt
            ? expanded
              ? 'error.100'
              : 'error.50'
            : expanded
            ? 'grey.100'
            : 'common.white',

          boxShadow: '6px 0 8px -8px rgba(0,0,0,0.35)',

          // ✅ borç şeridi
          ...(s.anyDebt && {
            pl: 1.25,
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              backgroundColor: 'error.main',
            },
          }),
        }}
      >
        {fmtDateTR(s.startAt)}
      </MDBox>
      <MDBox
        component="td"
        sx={{
          ...tdSx,
          pl: 1.65, // 🔥 hafif sağa kaydırır (şu an 0.75 idi)
        }}
      >
        {fmtTimeTR(s.startAt)}
      </MDBox>

      <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
        {safeText(s.activityName)}
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <Chip size="small" variant="outlined" label={safeText(s.sessionType)} />
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <Chip
          size="small"
          variant="outlined"
          label={s.isGroup ? `Grup (${s.participants.length})` : 'Bireysel'}
          color={s.isGroup ? 'info' : 'default'}
        />
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <span title={prev.full}>{prev.text}</span>
          {prev.total > 0 ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${prev.total} kişi`}
              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
            />
          ) : null}
        </MDBox>
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <Chip size="small" variant="outlined" label={yoklamaLabel} color={yoklamaColor} />
      </MDBox>

      <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
        {fmtTry(s.totalPaid)}
      </MDBox>

      <MDBox
        component="td"
        sx={{
          ...tdSx,
          fontWeight: 900,
          color: s.totalDebt > 0 ? 'error.main' : 'success.main',
        }}
      >
        {fmtTry(s.totalDebt)}
      </MDBox>

      {/* icon column: AccordionSummary zaten ikon koyuyor ama table hizası için burada boş bırakıyoruz */}
      <MDBox component="td" sx={{ ...tdSx, textAlign: 'center' }}>
        {/* AccordionSummary expandIcon burada render edilecek */}
      </MDBox>
    </MDBox>
  );
}

/* ---------------- main ---------------- */
export function StaffSessionsAccordion({ rows = [] }) {
  const sessions = useMemo(() => groupBySession(rows), [rows]);
  const [open, setOpen] = useState(() => new Set());

  const toggle = (sessionId) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  // ✅ aynı ölçü dili
  const thSx = {
    textAlign: 'left',
    px: 0.75,
    py: 0.6,
    fontSize: 10.25,
    fontWeight: 900,
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    whiteSpace: 'nowrap',
  };
  const tdSx = {
    px: 0.75,
    py: 0.45,
    fontSize: 10.75,
    borderBottom: '1px solid',
    borderColor: 'grey.100',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <MDBox>
      {/* ✅ Üst: seanslar tablo gibi */}
      <MDBox sx={{ overflowX: 'auto' }}>
        <MDBox
          component="table"
          sx={{
            width: '100%',
            minWidth: 1250,
            borderCollapse: 'separate', // ✅ daha stabil
            borderSpacing: 0, // ✅
            tableLayout: 'fixed',
          }}
        >
          <ColGroup cols={SUMMARY_COLS} /> {/* ✅ EKLENDİ */}
          <SummaryHeader thSx={thSx} />
          <MDBox component="tbody">
            {sessions.map((s) => {
              const expanded = open.has(s.sessionId);

              return (
                <MDBox key={s.sessionId} component="tr">
                  {/* ✅ Accordion’ı bir hücrenin içine alıp “table hissi”ni koruyoruz */}
                  <MDBox
                    component="td"
                    colSpan={10}
                    sx={{ p: 0, borderBottom: '1px solid', borderColor: 'grey.100' }}
                  >
                    <Accordion
                      expanded={expanded}
                      onChange={() => toggle(s.sessionId)}
                      disableGutters
                      elevation={0}
                      square
                      sx={{
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Icon sx={{ fontSize: 18 }}>
                            {expanded ? 'expand_less' : 'expand_more'}
                          </Icon>
                        }
                        sx={{
                          p: 0,
                          minHeight: 42,

                          '& .MuiAccordionSummary-content': { m: 0, p: 0 },
                          '& .MuiAccordionSummary-contentGutters': { m: 0 },
                          '& .MuiAccordionSummary-expandIconWrapper': { mr: 0.75, opacity: 0.75 },

                          cursor: 'pointer',
                        }}
                      >
                        {/* ✅ Summary satırını table gibi çiziyoruz */}
                        {/* ✅ Summary satırı: dış table'ın kolon sistemiyle aynı tr/td */}
                        <MDBox
                          component="table"
                          sx={{
                            width: '100%',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            tableLayout: 'fixed',
                          }}
                        >
                          <ColGroup cols={SUMMARY_COLS} /> {/* ✅ EKLENDİ */}
                          <MDBox component="tbody">
                            <SummaryRow s={s} tdSx={tdSx} expanded={expanded} />
                          </MDBox>
                        </MDBox>
                      </AccordionSummary>

                      {/* ✅ Detail: katılımcılar tablosu */}
                      <AccordionDetails
                        sx={{
                          pt: 1,
                          pb: 1.25,
                          bgcolor: 'grey.25',
                          borderTop: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      >
                        {' '}
                        <MDBox
                          sx={{
                            overflowX: 'auto',
                            maxHeight: 360, // ✅ detail çok uzarsa kaydır
                            overflowY: 'auto',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            bgcolor: 'common.white',
                          }}
                        >
                          {' '}
                          <MDBox
                            component="table"
                            sx={{
                              width: '100%',
                              minWidth: 1480,
                              borderCollapse: 'collapse',
                              tableLayout: 'fixed',
                            }}
                          >
                            <DetailsTableHeader thSx={thSx} />
                            <DetailsTableRows rows={s.participants} tdSx={tdSx} />
                          </MDBox>
                        </MDBox>
                      </AccordionDetails>
                    </Accordion>
                  </MDBox>
                </MDBox>
              );
            })}
            {sessions.length === 0 && (
              <MDBox component="tr">
                <MDBox component="td" colSpan={10} sx={{ py: 2 }}>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    Seans bulunamadı.
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
